import React from 'react';
import { QuizPreviewSummaryProps } from '../../types';

const QuizPreviewSummary: React.FC<QuizPreviewSummaryProps> = ({
  isQuizGenerated,
  isGenerating,
  quizPreview,
  quizConfig,
  examOptions,
  subjectOptions,
  chapterOptions,
  topicOptions,
  difficultyOptions,
  onStartQuiz,
  onResetForm
}) => {
  return (
    <div className="row g-4 mt-4">
      <div className="col-12">
        {!isQuizGenerated && !isGenerating ? (
          <div className="card border-0 shadow-sm" style={{ borderRadius: '20px' }}>
            <div className="card-body p-4 text-center">
              <div className="mb-3">
                <i className="fas fa-clipboard-list fa-3x text-muted"></i>
              </div>
              <h5 className="text-muted">Quiz Preview</h5>
              <p className="text-muted small mb-0">
                Configure your quiz settings and click "Generate Custom Quiz" to see the preview
              </p>
            </div>
          </div>
        ) : isQuizGenerated ? (
          <>
            {/* Success Header */}
            <div className="text-center mb-4">
              <div className="mb-3">
                <i className="fas fa-check-circle fa-4x text-success"></i>
              </div>
              <h2 className="fw-bold text-success mb-2">Quiz Generated Successfully!</h2>
              <p className="lead text-muted">
                Your personalized quiz is ready. Review the details below and start when you're ready.
              </p>
            </div>

            {/* Quiz Preview Card */}
            <div className="card border-0 shadow-sm" style={{ borderRadius: '20px' }}>
              <div className="card-header bg-transparent border-0 p-4">
                <h5 className="fw-bold mb-0">
                  <i className="fas fa-eye me-2 text-success"></i>
                  Quiz Preview
                </h5>
              </div>
              <div className="card-body p-4">
                {quizPreview && (
                  <>
                    <h6 className="fw-bold mb-3">{quizPreview.title}</h6>
                    <p className="text-muted mb-3">{quizPreview.description}</p>
                    
                    <div className="row g-3 mb-4">
                      <div className="col-6">
                        <div className="text-center p-3 bg-light rounded">
                          <i className="fas fa-clock text-primary mb-1"></i>
                          <div className="fw-bold">{quizPreview.estimatedTime}</div>
                          <small className="text-muted">Duration</small>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="text-center p-3 bg-light rounded">
                          <i className="fas fa-star text-warning mb-1"></i>
                          <div className="fw-bold">{quizPreview.totalMarks}</div>
                          <small className="text-muted">Total Marks</small>
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <h6 className="fw-semibold mb-2">Quiz Details:</h6>
                      <ul className="list-unstyled small">
                        <li className="mb-1">
                          <i className="fas fa-check text-success me-2"></i>
                          <strong>Exam:</strong> {examOptions.find(e => e.value === quizConfig.exam)?.label}
                        </li>
                        <li className="mb-1">
                          <i className="fas fa-check text-success me-2"></i>
                          <strong>Subjects:</strong> {quizConfig.subjects.map(s => 
                            subjectOptions.find(sub => sub.value === s)?.label
                          ).join(', ')}
                        </li>
                        {quizConfig.chapters.length > 0 && (
                          <li className="mb-1">
                            <i className="fas fa-check text-success me-2"></i>
                            <strong>Chapters:</strong> {quizConfig.chapters.map(c => 
                              chapterOptions.find(ch => ch.value === c)?.label
                            ).join(', ')}
                          </li>
                        )}
                        {quizConfig.topics.length > 0 && (
                          <li className="mb-1">
                            <i className="fas fa-check text-success me-2"></i>
                            <strong>Topics:</strong> {quizConfig.topics.map(t => 
                              topicOptions.find(tp => tp.value === t)?.label
                            ).join(', ')}
                          </li>
                        )}
                        <li className="mb-1">
                          <i className="fas fa-check text-success me-2"></i>
                          <strong>Difficulty:</strong> {difficultyOptions.find(d => d.value === quizConfig.difficulty)?.label}
                        </li>
                        <li className="mb-1">
                          <i className="fas fa-check text-success me-2"></i>
                          <strong>Questions:</strong> {quizPreview?.actualQuestions || quizConfig.numberOfQuestions}
                          {quizPreview?.actualQuestions && quizPreview.actualQuestions !== quizConfig.numberOfQuestions && 
                            <small className="text-muted"> (requested: {quizConfig.numberOfQuestions})</small>
                          }
                        </li>
                      </ul>
                    </div>

                    <div className="d-grid gap-2">
                      <button
                        className="btn btn-success btn-lg"
                        style={{ borderRadius: '12px' }}
                        onClick={onStartQuiz}
                      >
                        <i className="fas fa-play me-2"></i>
                        Start Quiz
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        style={{ borderRadius: '12px' }}
                        onClick={onResetForm}
                      >
                        <i className="fas fa-redo me-2"></i>
                        Create New Quiz
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default QuizPreviewSummary;